import React, { Fragment, HTMLAttributes, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ModalTitle, Button, CloseIcon, btnLevel } from '..';

const { Overlay } = Dialog;

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  action?: boolean;
  btnActionDisabled?: boolean;
  btnActionText?: any;
  btnLevel?: btnLevel;
  btnText?: string;
  btnType?: 'primary' | 'secondary' | 'form' | 'text';
  footer?: boolean;
  footerPrimary?: any;
  footerCancel?: boolean;
  primaryDisabled?: boolean;
  primaryAction?: React.MouseEventHandler<HTMLButtonElement>;
  cancelAction?: React.MouseEventHandler<HTMLButtonElement>;
  btnAction?: React.MouseEventHandler<HTMLButtonElement>;
  title?: string;
  variant: 'sm' | 'default' | 'lg' | 'xl';
  primaryFooterVariant?: 'light' | 'dark';
  handleCancelFunction?: React.Dispatch<React.SetStateAction<boolean>>;
  handleForm?: (x?: any) => any | void;
  primaryClose?: boolean;
  actionClose?: boolean;
  onOpen?: (x?: any) => any | void;
  onClose?: (x?: any) => any | void;
  altBtn?: any;
  disableBackdropClick?: boolean;
  openOnLoad?: boolean;
  disableX?: boolean;
  hideX?: boolean;
  disableModal?: boolean;
  width?: string;
  modalClassNames?: string;
}

export const Modal = ({
  children,
  action,
  btnActionText,
  btnLevel,
  btnText,
  btnType,
  footer,
  footerPrimary,
  footerCancel,
  primaryAction,
  cancelAction,
  btnAction,
  title,
  variant,
  btnActionDisabled,
  primaryDisabled,
  primaryFooterVariant,
  handleCancelFunction,
  handleForm,
  primaryClose,
  actionClose,
  onOpen,
  onClose,
  altBtn,
  disableBackdropClick,
  openOnLoad = false,
  disableX,
  hideX,
  disableModal,
  width,
  modalClassNames,
  ...rest
}: ModalProps) => {
  /* Checking for missing required props */
  // if (!btnText)
  //   throw new Error(
  //     `You need to pass a prop called "btnText" of type string to the Modal component. To learn more about the list of required props for the
  //     Modal component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-modals`
  //   );
  // if (!btnType)
  //   throw new Error(
  //     `You need to pass a prop called "btnType" of type string to the Modal component. To learn more about the list of required props for the
  //     Modal component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-modals`
  //   );
  // if (!variant)
  //   throw new Error(
  //     `You need to pass a prop called "variant" of type 'sm' | 'default' | 'lg' | 'xl' to the Modal component. To learn more about the list of required props for the
  //     Modal component go to Maverick documentation at: https://thryvlabs-maverick.netlify.app/?path=/docs/ui-modals`
  //   );
  /* Checking for missing required props */
  const [isOpen, setIsOpen] = useState<boolean>(openOnLoad);

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen && openOnLoad) {
      setIsOpen(true);
    }
  }, [openOnLoad]);

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }

    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen]);

  const renderFooter = footer ? (
    <footer className="bottom-0 w-full sm:fixed sm:h-[100px] py-[10px] sm:left-0">
      <div className="w-full flex justify-end items-center gap-4 sm:justify-center sm:h-[100px]">
        {footerCancel && (
          <Button
            variant="text"
            textVariant="light"
            level={2}
            // disabled
            onClick={(e) => {
              !cancelAction &&
                handleCancelFunction &&
                handleCancelFunction(false);
              cancelAction && cancelAction(e);
              handleCloseModal();
            }}
          >
            CANCEL
          </Button>
        )}
        {footerPrimary && (
          <Button
            variant="text"
            level={2}
            onClick={(e) => {
              primaryAction && primaryAction(e);
              handleForm && handleForm(e);
              primaryClose && handleCloseModal();
            }}
            disabled={primaryDisabled}
            textVariant={primaryFooterVariant}
          >
            {footerPrimary}
          </Button>
        )}
        {action && (
          <Button
            variant="primary"
            onClick={(e) => {
              btnAction && btnAction(e);
              handleForm && handleForm(e);
              actionClose && handleCloseModal();
            }}
            disabled={btnActionDisabled}
          >
            {btnActionText}
          </Button>
        )}
      </div>
    </footer>
  ) : null;

  return (
    <>
      {!altBtn && btnType ? (
        <Button
          variant={btnType}
          level={btnLevel || undefined}
          type="button"
          onClick={handleOpenModal}
          className="focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
          disabled={disableModal}
        >
          {btnText}
        </Button>
      ) : (
        <button
          type="button"
          onClick={handleOpenModal}
          className="focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
          disabled={disableModal}
        >
          {altBtn}
        </button>
      )}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          open={isOpen}
          onClose={disableBackdropClick ? () => {} : handleCloseModal}
          className={`fixed z-100 inset-0 w-screen h-screen overflow-y-auto`}
        >
          <div
            className="min-h-screen px-4 sm:px-0 text-center z-100 sm:overflow-x-hidden"
            {...rest}
          >
            <Transition.Child
              as="div"
              enter="ease-in-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Overlay className="fixed inset-0 bg-thryv-black-500 opacity-60 p-[30px]" />
            </Transition.Child>
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {/* This element is to trick the browser into centering the modal contents. */}
              <span
                className="inline-block h-screen align-middle md:align-top lg:align-middle xl:align-middle sm:hidden"
                aria-hidden="true"
              >
                &#8203;
              </span>

              <div
                className={`inline-block bg-white box-content
              ${
                variant === 'sm'
                  ? 'w-300px lg:w-2/4 md:w-2/4 sm:w-2/4 h-auto py-[12px] px-[16px]'
                  : variant === 'default'
                  ? 'w-[500px] lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
                  : variant === 'lg'
                  ? 'w-3/4 lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
                  : 'w-3/4 lg:w-3/4 md:w-3/4 h-auto py-[22px] px-[32px]'
              }
              p-2 my-4 overflow-hidden text-left align-middle transition-all transform shadow-thryv rounded border border-thryv-gray-light-400 sm:w-[100%] sm:px-0 sm:h-screen sm:my-0 sm:p-0 sm:py-0 ${
                modalClassNames || ''
              }`}
                /* DO NOT DELETE id="modal". IT IS USED TO ADD EVENT LISTENER CORRECTLY WHEN THE DATEPICKER IS IN A MODAL */
                id="modal"
                style={width ? { width: width } : undefined}
              >
                <div className="sm:p-4 sm:h-screen flex-col items-between">
                  <div className="w-full flex justify-between items-start gap-[6px]">
                    {variant === 'sm' ? (
                      <ModalTitle variant="small">{title}</ModalTitle>
                    ) : (
                      <ModalTitle variant="primary">{title}</ModalTitle>
                    )}
                    {!hideX ? (
                      <button
                        className="bg-none border-none"
                        onClick={!disableX ? handleCloseModal : () => {}}
                        aria-label="Close Modal"
                        aria-describedby="Click to close modal"
                      >
                        <CloseIcon
                          width={variant === 'sm' ? '13' : undefined}
                          height={variant === 'sm' ? '13' : undefined}
                        />
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-[13px] sm:overflow-y-scroll sm:h-[calc(100%-108px)]">
                    {children}
                  </div>
                  {renderFooter}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};
